import React, { useState } from 'react';
import { X, Send, CornerDownLeft } from 'lucide-react';
import { api } from '../api/client';
import { useTranslation } from '../i18n';
import { shortName } from '../utils/displayName';

/**
 * Shared threaded comment UI, extracted from PostCard so it can be reused by any
 * comment-bearing entity (Posts, Movement Stories, …).
 *
 * The data source is injected via `commentApi`:
 *   add(content, parentId) → Promise<{ comment }>
 *   remove(commentId)      → Promise
 *
 * When omitted it defaults to the Posts endpoints keyed on `targetId`, so the
 * original PostCard behaviour is preserved.
 */

function timeAgo(d, t) {
  if (!d) return '';
  const m = Math.floor((Date.now() - new Date(d)) / 60000);
  if (m < 1) return t('common.justNow');
  if (m < 60) return t('common.mAgo', m);
  const h = Math.floor(m / 60); if (h < 24) return t('common.hAgo', h);
  return t('common.dAgo', Math.floor(h / 24));
}
function getInitials(n = '') { return n.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?'; }

function postsCommentApi(targetId) {
  return {
    add:    (content, parentId) => api.post(`/api/posts/${targetId}/comments`, { content, parentId }),
    remove: (commentId)         => api.delete(`/api/posts/${targetId}/comments/${commentId}`),
  };
}

function Comment({ comment, commentApi, onDelete, onCountChange, depth = 0 }) {
  const { t } = useTranslation();
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading]     = useState(false);
  const [replies, setReplies]     = useState(comment.replies || []);

  async function submitReply() {
    if (!replyText.trim()) return;
    setLoading(true);
    try {
      const d = await commentApi.add(replyText.trim(), comment.id);
      setReplies(r => [...r, d.comment]);
      setReplyText(''); setShowReply(false);
      onCountChange && onCountChange(1);
    } catch {}
    finally { setLoading(false); }
  }

  async function handleDelete(commentId, parentId) {
    await commentApi.remove(commentId);
    if (parentId) setReplies(rs => rs.filter(r => r.id !== commentId));
    onCountChange && onCountChange(-1);
    onDelete && onDelete(commentId, parentId);
  }

  return (
    <div className={`comment-item${depth > 0 ? ' comment-reply' : ''}`}>
      <div className="comment-avatar">{getInitials(comment.author?.name)}</div>
      <div className="comment-body">
        <div className="comment-header-row">
          <span className="comment-author">{shortName(comment.author?.name)}</span>
          <span className="comment-time">{timeAgo(comment.createdAt, t)}</span>
          <button className="comment-delete" onClick={() => handleDelete(comment.id, comment.parentId)} title={t('common.delete')}>
            <X size={11}/>
          </button>
        </div>
        <p className="comment-text">{comment.content}</p>
        {depth === 0 && (
          <button className="comment-reply-btn" onClick={() => setShowReply(s => !s)}>
            <CornerDownLeft size={12}/> {showReply ? t('feed.cancelReply') : t('feed.reply')}
          </button>
        )}
        {showReply && (
          <div className="reply-form">
            <input className="comment-input" placeholder={t('feed.writeReply')} value={replyText}
              onChange={e => setReplyText(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && submitReply()}/>
            <button className="comment-submit" onClick={submitReply} disabled={loading}>
              {loading ? <span className="spinner" style={{ width: 12, height: 12, borderWidth: 1.5 }}/> : <Send size={13}/>}
            </button>
          </div>
        )}
        {replies.length > 0 && (
          <div className="comment-replies">
            {replies.map(r => (
              <Comment key={r.id} comment={r} commentApi={commentApi} depth={depth + 1}
                onDelete={() => setReplies(rs => rs.filter(x => x.id !== r.id))}
                onCountChange={onCountChange}/>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CommentSection({ targetId, commentApi, initialComments = [], onCountChange }) {
  const { t } = useTranslation();
  const resolvedApi = commentApi || postsCommentApi(targetId);
  const [comments, setComments] = useState(initialComments);
  const [text, setText]         = useState('');
  const [loading, setLoading]   = useState(false);

  async function submit() {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const d = await resolvedApi.add(text.trim(), null);
      setComments(c => [...c, d.comment]); setText('');
      onCountChange && onCountChange(1);
    } catch {}
    finally { setLoading(false); }
  }

  function handleDelete(commentId, parentId) {
    if (!parentId) {
      const c = comments.find(c => c.id === commentId);
      const total = 1 + (c?.replies?.length || 0);
      setComments(cs => cs.filter(c => c.id !== commentId));
      onCountChange && onCountChange(-total);
    }
  }

  return (
    <div className="comment-section">
      {comments.map(c => (
        <Comment key={c.id} comment={c} commentApi={resolvedApi}
          onDelete={handleDelete} onCountChange={onCountChange}/>
      ))}
      <div className="comment-form">
        <input className="comment-input" placeholder={t('feed.writeComment')} value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && submit()}/>
        <button className="comment-submit" onClick={submit} disabled={loading}>
          {loading ? <span className="spinner" style={{ width: 12, height: 12, borderWidth: 1.5 }}/> : <Send size={13}/>}
        </button>
      </div>
    </div>
  );
}
